import React, { useEffect, useMemo, useRef, useState } from 'react';
import { xor, isNil, uniqBy } from 'lodash';
import { FieldProps } from 'formik';
import { createFilter, InputActionMeta } from 'react-select';
import { SelectSizes } from '../../../inputs/Select/select-sizes.enum';
import { Option } from '../../types';
import { IFilterField } from '../../interfaces';
import { StyledSelect } from './select-styles';
import { ListFooter, ListHeader, CheckboxOption, MenuList } from './MultiSelectList';
import { DropdownIndicator } from './DropdownIndicator';

const matchesSearchTerm = createFilter();

interface IMultiSelectProps extends IFilterField {
  size?: SelectSizes;
  options: Option[];
  closePopover(): void;
  form: FieldProps['form'];
  field: FieldProps['field'];
  onSave?: (value: any[]) => void;
  loadOptions?: (searchTerm: string) => Option[];
  onOpen?: () => void;
  minimumSelectedOptions?: number;
}

export const MultiSelect: React.FC<IMultiSelectProps> = ({
  field,
  form,
  options = [],
  size = SelectSizes.Medium,
  name,
  closePopover,
  onSave,
  loadOptions,
  onOpen,
  minimumSelectedOptions,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<Option[] | null>(null);
  const [inputValue, setInputValue] = useState('');
  const didSyncSelection = useRef(false);
  const fieldValue =
    selectedOptions && selectedOptions?.length > 0 ? selectedOptions.map(({ value }) => value) : undefined;

  const isAsync = Boolean(loadOptions);

  const matchingOptions = useMemo(
    () =>
      isAsync
        ? options
        : options.filter(option =>
            matchesSearchTerm({ label: option.label, value: String(option.value), data: option }, inputValue),
          ),
    [options, inputValue, isAsync],
  );

  const isSaveDisabled = useMemo(() => {
    if (isNil(selectedOptions)) return true;

    if (!isNil(minimumSelectedOptions) && selectedOptions.length < minimumSelectedOptions) return true;

    const mappedSelected = selectedOptions.map(({ value }) => value);

    return xor(mappedSelected, field.value).length === 0;
  }, [selectedOptions]);

  const isOptionSelected = (option: Option) => selectedOptions?.some(({ value }) => value === option.value) ?? false;
  const hasSelection = selectedOptions !== null && selectedOptions.length > 0;

  const handleInputChange = (value: string, { action }: InputActionMeta) => {
    if (action === 'input-change') setInputValue(value);
  };

  useEffect(() => {
    onOpen?.();
  }, []);

  useEffect(() => {
    if (didSyncSelection.current) return;

    if (!options.length && field.value?.length) return;

    setSelectedOptions(options.filter(({ value }) => field.value?.includes(value)));
    didSyncSelection.current = true;
  }, [options, field.value]);

  const handleSave = () => {
    form.setFieldValue(field.name, fieldValue, false);
    onSave?.(fieldValue ?? []);
    closePopover();
  };

  const MenuListHeader = (
    <ListHeader
      onClear={() => setSelectedOptions([])}
      onSelectAll={() => setSelectedOptions(uniqBy([...(selectedOptions ?? []), ...matchingOptions], 'value'))}
      numberOfOptions={matchingOptions.length}
      hasSelection={hasSelection}
      isSelectAllDisabled={matchingOptions.every(isOptionSelected)}
    />
  );

  const MenuListFooter = <ListFooter isSaveDisabled={isSaveDisabled} onSave={handleSave} onCancel={closePopover} />;

  return (
    <StyledSelect
      isMulti
      menuIsOpen
      size={size}
      options={options}
      isClearable={false}
      placeholder={`Search`}
      value={selectedOptions}
      inputValue={isAsync ? undefined : inputValue}
      onInputChange={isAsync ? undefined : handleInputChange}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      hideSearch={options.length < 10}
      subject={`filter-${name}`}
      onChange={setSelectedOptions}
      controlShouldRenderValue={false}
      components={{ Option: CheckboxOption, MenuList, MenuListHeader, MenuListFooter, DropdownIndicator }}
      loadOptions={loadOptions}
    />
  );
};
